import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  IconButton,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Tv as TvIcon,
  Public as PublicIcon,
  LocationOn as LocationOnIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { channelsAPI } from '../services/api';
import axios from 'axios';

// Компонент заголовка с поиском (без dropdown)
const SearchTableHeader = ({ label, value, onChange, placeholder }) => {
  return (
    <TableCell sx={{ p: 1, minWidth: 120 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          {label}
        </Typography>
        <TextField
          size="small"
          placeholder={placeholder || 'Поиск...'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          InputProps={{
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onChange('')}
                  sx={{ p: 0.25 }}
                >
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.75rem',
            },
            '& .MuiInputBase-input': {
              py: 0.5,
              px: 0.5
            }
          }}
        />
      </Box>
    </TableCell>
  );
};

// Компонент заголовка с селектором статуса
const StatusFilterHeader = ({ label, value, onChange }) => {
  return (
    <TableCell sx={{ p: 1, minWidth: 120 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          {label}
        </Typography>
        <TextField
          select
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          SelectProps={{
            native: true,
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.75rem',
            },
            '& .MuiInputBase-input': {
              py: 0.5,
              px: 0.5
            }
          }}
        >
          <option value="all">Все</option>
          <option value="active">Активен</option>
          <option value="inactive">Не активен</option>
        </TextField>
      </Box>
    </TableCell>
  );
};

// Компонент заголовка с селектором использования в плейлистах
const InPlaylistsFilterHeader = ({ label, value, onChange }) => {
  return (
    <TableCell sx={{ p: 1, minWidth: 100 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          {label}
        </Typography>
        <TextField
          select
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          SelectProps={{
            native: true,
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.75rem',
            },
            '& .MuiInputBase-input': {
              py: 0.5,
              px: 0.5
            }
          }}
        >
          <option value="all">Все</option>
          <option value="yes">Да</option>
          <option value="no">Нет</option>
        </TextField>
      </Box>
    </TableCell>
  );
};

const AllChannelsPage = () => {
  const navigate = useNavigate();

  const [channels, setChannels] = useState([]);
  const [channelsInPlaylists, setChannelsInPlaylists] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, channel: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Состояния для фильтрации
  const [nameSearch, setNameSearch] = useState('');
  const [urlSearch, setUrlSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [inPlaylistsFilter, setInPlaylistsFilter] = useState('all'); // 'all', 'yes', 'no'

  useEffect(() => {
    loadChannels();
    loadChannelsInPlaylists();
  }, []);

  const loadChannels = async () => {
    try {
      setLoading(true);
      const response = await channelsAPI.getAll();
      setChannels(response.data);
    } catch (error) {
      console.error('Ошибка загрузки каналов:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка загрузки каналов',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadChannelsInPlaylists = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/channels/in-playlists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChannelsInPlaylists(new Set(response.data.channel_ids));
    } catch (error) {
      console.error('Ошибка загрузки информации о каналах в плейлистах:', error);
    }
  };

  // Фильтрация каналов
  const filteredChannels = channels.filter(channel => {
    // Фильтр по названию
    if (nameSearch && !channel.name?.toLowerCase().includes(nameSearch.toLowerCase())) {
      return false;
    }
    // Фильтр по URL
    if (urlSearch && !channel.url?.toLowerCase().includes(urlSearch.toLowerCase())) {
      return false;
    }
    // Фильтр по статусу
    if (statusFilter === 'active' && !channel.is_active) {
      return false;
    }
    if (statusFilter === 'inactive' && channel.is_active) {
      return false;
    }
    // Фильтр по использованию в плейлистах
    const isInPlaylist = channelsInPlaylists.has(channel.id);
    if (inPlaylistsFilter === 'yes' && !isInPlaylist) {
      return false;
    }
    if (inPlaylistsFilter === 'no' && isInPlaylist) {
      return false;
    }
    return true;
  }).sort((a, b) => {
    // Сортировка: сначала используемые в плейлистах, потом неиспользуемые
    const aInPlaylist = channelsInPlaylists.has(a.id);
    const bInPlaylist = channelsInPlaylists.has(b.id);
    
    if (aInPlaylist && !bInPlaylist) return -1;
    if (!aInPlaylist && bInPlaylist) return 1;
    return 0;
  });

  const handleDeleteClick = (channel) => {
    setDeleteDialog({ open: true, channel });
  };

  const handleDeleteConfirm = async () => {
    const { channel } = deleteDialog;
    if (!channel) return;

    try {
      await channelsAPI.delete(channel.id);
      setSnackbar({
        open: true,
        message: 'Канал успешно удален из базы данных',
        severity: 'success'
      });
      setDeleteDialog({ open: false, channel: null });
      loadChannels();
    } catch (error) {
      console.error('Ошибка удаления канала:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Ошибка удаления канала',
        severity: 'error'
      });
    }
  };

  const renderGeoRestrictions = (geoRestrictions) => {
    if (!geoRestrictions || geoRestrictions.type === 'world') {
      return (
        <Chip
          icon={<PublicIcon />}
          label="Весь мир"
          size="small"
          color="success"
          variant="outlined"
        />
      );
    }

    if (geoRestrictions.type === 'whitelist') {
      return (
        <Chip
          icon={<LocationOnIcon />}
          label={`Доступно: ${geoRestrictions.countries?.join(', ') || 'не указано'}`}
          size="small"
          color="primary"
          variant="outlined"
        />
      );
    }

    if (geoRestrictions.type === 'blacklist') {
      return (
        <Chip
          icon={<LocationOnIcon />}
          label={`Заблокировано: ${geoRestrictions.countries?.join(', ') || 'не указано'}`}
          size="small"
          color="error"
          variant="outlined"
        />
      );
    }

    return null;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TvIcon />
          Все каналы
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/playlists/1/channels/new')}
        >
          Создать канал
        </Button>
      </Box>

      {/* Таблица каналов */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ p: 1, width: 60 }}>Иконка</TableCell>
              <SearchTableHeader 
                label="Название" 
                value={nameSearch} 
                onChange={setNameSearch}
                placeholder="Поиск по названию..."
              />
              <SearchTableHeader 
                label="URL" 
                value={urlSearch} 
                onChange={setUrlSearch}
                placeholder="Поиск по URL..."
              />
              <StatusFilterHeader 
                label="Статус" 
                value={statusFilter} 
                onChange={setStatusFilter}
              />
              <InPlaylistsFilterHeader 
                label="В плейлистах" 
                value={inPlaylistsFilter} 
                onChange={setInPlaylistsFilter}
              />
              <TableCell align="right" sx={{ p: 1, width: 120 }}>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredChannels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Каналы не найдены
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredChannels.map((channel) => {
                const isInPlaylist = channelsInPlaylists.has(channel.id);
                return (
                  <TableRow
                    key={channel.id}
                    sx={{
                      opacity: channel.is_active ? 1 : 0.6,
                      backgroundColor: isInPlaylist ? 'rgba(76, 175, 80, 0.05)' : 'inherit',
                      '&:hover': {
                        backgroundColor: isInPlaylist ? 'rgba(76, 175, 80, 0.1)' : 'action.hover'
                      }
                    }}
                  >
                    <TableCell sx={{ p: 1 }}>
                      <Avatar
                        src={channel.icon}
                        alt={channel.name}
                        sx={{ width: 32, height: 32 }}
                      >
                        <TvIcon />
                      </Avatar>
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        {channel.name || 'Без названия'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.875rem'
                        }}
                      >
                        {channel.url || 'Не указан'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Chip
                        label={channel.is_active ? 'Активен' : 'Неактивен'}
                        size="small"
                        color={channel.is_active ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Tooltip title={isInPlaylist ? 'Используется в плейлистах' : 'Не используется'}>
                        {isInPlaylist ? (
                          <CheckCircleIcon color="success" sx={{ fontSize: '1.2rem' }} />
                        ) : (
                          <CancelIcon color="disabled" sx={{ fontSize: '1.2rem' }} />
                        )}
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ p: 1 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/channels/${channel.id}/edit`)}
                        title="Редактировать"
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(channel)}
                        title="Удалить из базы данных"
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог подтверждения удаления */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, channel: null })}>
        <DialogTitle>Подтвердите удаление</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Внимание!</strong> Это действие нельзя отменить.
          </Alert>
          <Typography>
            Вы уверены, что хотите удалить канал <strong>"{deleteDialog.channel?.name}"</strong> из базы данных?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Канал будет удален из всех плейлистов и полностью из базы данных.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, channel: null })}>
            Отмена
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
          >
            Удалить навсегда
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AllChannelsPage;
